import { useTranslation } from 'react-i18next';
import type { MyOrderSummary, OrderStatus } from '../../types/order';
import { formatTimeWindow, presentWindow } from '../../utils/timeWindow';
import { formatPriceCents } from '../../utils/currency';

// Per-status badge presentation, grouped by lifecycle stage. Colors map to the
// Ionic brand palette via the `text-brand-*` / `border-brand-*` helpers
// (src/styles/utils.scss), so they're theme-aware. `labelKey` resolves under
// the profile namespace.
const STATUS_META: Record<OrderStatus, { color: string; labelKey: string }> = {
  held: { color: 'warning', labelKey: 'orders.status.held' },
  confirmed: { color: 'warning', labelKey: 'orders.status.confirmed' },
  paid: { color: 'primary', labelKey: 'orders.status.paid' },
  received: { color: 'primary', labelKey: 'orders.status.received' },
  accepted: { color: 'primary', labelKey: 'orders.status.accepted' },
  ready_to_pickup: { color: 'primary', labelKey: 'orders.status.readyToPickup' },
  completed: { color: 'success', labelKey: 'orders.status.completed' },
  canceled: { color: 'danger', labelKey: 'orders.status.canceled' },
  no_show: { color: 'danger', labelKey: 'orders.status.noShow' },
  abandoned: { color: 'medium', labelKey: 'orders.status.abandoned' },
  expired: { color: 'medium', labelKey: 'orders.status.expired' },
};

interface OrderHistoryRowProps {
  order: MyOrderSummary;
  onClick: () => void;
}

/**
 * Compact row for the buyer's order history — dish thumbnail + title + order
 * status badge, over a meta line (amount paid · qty · pickup window / order
 * date) and who they bought from.
 */
export const OrderHistoryRow: React.FC<OrderHistoryRowProps> = ({ order, onClick }) => {
  const { t } = useTranslation('profile');
  const { status, quantity, totalPaid, createdAt, listing } = order;
  const meta = STATUS_META[status];

  // The window this order uses, not `timeWindow` (which spans every method the
  // listing offers). Falls back to it for payloads that predate the split.
  const orderWindow =
    presentWindow(order.fulfillmentMethod === 'dropoff' ? listing?.dropoffWindow : listing?.pickupWindow) ??
    listing?.timeWindow ??
    null;
  const windowLabel = formatTimeWindow(orderWindow);
  const dateLabel = windowLabel || t('orders.ordered', { date: new Date(createdAt).toLocaleDateString() });

  const metaParts = [
    totalPaid > 0 ? formatPriceCents(totalPaid) : null,
    t('orders.qty', { count: quantity }),
    dateLabel,
  ].filter(Boolean);

  const listerName = listing?.listerSummary?.businessName || listing?.listerSummary?.displayName;

  return (
    // Shares the `hp-history-row` container + reflow + separator styling with
    // ListingHistoryRow (My Kitchen). See global.scss.
    <button
      type="button"
      onClick={onClick}
      className="hp-history-row block w-full p-3 text-left hover:bg-surface-100"
    >
      <div className="hp-history-row__inner">
        <div
          className="hp-history-row__thumb h-16 w-16 shrink-0 overflow-hidden rounded-lg"
          style={{ backgroundColor: 'var(--ion-color-light)' }}
        >
          {listing?.thumbnailUrl || listing?.imageUrl ? (
            <img
              src={listing.thumbnailUrl ?? listing.imageUrl ?? ''}
              alt={listing.title}
              className="h-full w-full object-cover"
            />
          ) : null}
        </div>

        <div className="hp-history-row__body min-w-0 flex-1">
          <div className="hp-history-row__head">
            <h3 className="hp-history-row__title font-medium">
              {listing?.title ?? t('orders.unknownDish')}
            </h3>
            <span
              className={`hp-history-row__badge shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium border-brand-${meta.color} text-brand-${meta.color}`}
            >
              {t(
                // Drop-off "ready" means the chef's run is under way.
                status === 'ready_to_pickup' && order.fulfillmentMethod === 'dropoff'
                  ? 'orders.status.outForDelivery'
                  : meta.labelKey,
              )}
            </span>
          </div>
          <p className="hp-history-row__meta mt-1 text-xs text-muted-foreground">
            {metaParts.join(' · ')}
          </p>
          {listerName && (
            <p className="hp-history-row__meta mt-0.5 text-xs text-muted-foreground">
              {t('orders.from', { name: listerName })}
            </p>
          )}
        </div>
      </div>
    </button>
  );
};
